import { useState } from 'react';
import type { PhotoIdentification, Product, ProductListEntry } from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import {
  EMPTY_PRODUCT_DRAFT,
  ProductFields,
  applyVisionSuggestion,
  changedProductFields,
  draftToProductInput,
  needsBarcodeLink,
  productToDraft,
  type ProductDraft,
} from '../components/ProductFields.js';
import { EmptyState, Modal, useAsync, useToast } from '../components/ui.js';

/**
 * Cadastro de produtos: o catalogo de casa, fora do fluxo de entrada. E aqui
 * que se corrige o que a IA ou o catalogo publico trouxe torto, vincula um
 * codigo de barras que faltava e apaga o que nao existe mais — sempre valendo
 * para todas as dispensas, porque o produto e um so.
 */
export function ProductsPage() {
  const toast = useToast();
  const { refresh } = useAppData();
  const [query, setQuery] = useState('');
  const [busca, setBusca] = useState('');
  const lista = useAsync(() => api.listProducts(busca), [busca]);
  const [editando, setEditando] = useState<Product | null>(null);
  const [criando, setCriando] = useState(false);

  const produtos = lista.data ?? [];

  async function apagar(produto: ProductListEntry) {
    const emUso = produto.itemCount > 0;
    const pergunta = emUso
      ? `"${produto.name}" ainda esta guardado (${produto.totalQuantity} ${produto.unit} em ${produto.itemCount} ${produto.itemCount === 1 ? 'lugar' : 'lugares'}). Apagar o produto remove tambem esses itens das dispensas. Continuar?`
      : `Apagar "${produto.name}" do catalogo?`;
    if (!window.confirm(pergunta)) return;
    try {
      await api.deleteProduct(produto.id, emUso);
      toast('Produto apagado.', 'ok');
      lista.reload();
      await refresh();
    } catch (error) {
      toast((error as Error).message, 'error');
    }
  }

  async function aoSalvar() {
    setEditando(null);
    setCriando(false);
    lista.reload();
    await refresh();
  }

  return (
    <main className="page">
      <div className="page-head">
        <h1>Produtos</h1>
        <span className="badge">
          {produtos.length} {produtos.length === 1 ? 'produto' : 'produtos'}
        </span>
        <div className="spacer" />
        <button type="button" className="btn btn-primary" onClick={() => setCriando(true)}>
          + Novo produto
        </button>
      </div>

      <form
        className="row"
        style={{ alignItems: 'flex-end', marginBottom: 16 }}
        onSubmit={(event) => {
          event.preventDefault();
          setBusca(query);
        }}
      >
        <div className="field" style={{ marginBottom: 0, flex: '3 1 240px' }}>
          <label htmlFor="produto-busca">Procurar</label>
          <input
            id="produto-busca"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Nome, marca, tipo, categoria ou codigo de barras"
          />
        </div>
        <button type="submit" className="btn" style={{ flex: '0 0 auto' }}>
          Buscar
        </button>
        {busca ? (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ flex: '0 0 auto' }}
            onClick={() => {
              setQuery('');
              setBusca('');
            }}
          >
            Limpar
          </button>
        ) : null}
      </form>

      {lista.loading ? <p className="muted">Carregando…</p> : null}
      {lista.error ? <p style={{ color: 'var(--danger)' }}>{lista.error}</p> : null}

      {!lista.loading && produtos.length === 0 ? (
        <EmptyState
          icon="🏷"
          title={busca ? 'Nenhum produto encontrado' : 'Catalogo vazio'}
          hint={
            busca
              ? 'Tente outro termo — a busca cobre nome, marca, tipo, categoria e codigo.'
              : 'Os produtos entram no catalogo sozinhos quando voce da entrada. Voce tambem pode cadastrar um aqui.'
          }
        />
      ) : null}

      {produtos.length > 0 ? (
        <ul className="list">
          {produtos.map((produto) => (
            <li key={produto.id} className="item">
              <div className="photo">
                {produto.photoUrl ? (
                  <img
                    src={produto.photoUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                  />
                ) : (
                  '🏷'
                )}
              </div>
              <div className="info">
                <div className="title">{produto.name}</div>
                <div className="sub">
                  {produto.brand ? <span>{produto.brand}</span> : null}
                  {produto.productType ? <span className="badge">{produto.productType}</span> : null}
                  {produto.size ? <span className="badge">{produto.size}</span> : null}
                  {produto.category ? (
                    <span className="badge" title="Categoria — define a validade estimada">
                      {produto.category}
                    </span>
                  ) : null}
                  {produto.barcode ? (
                    <span className="small muted">{produto.barcode}</span>
                  ) : (
                    <span className="badge badge-warn">sem codigo</span>
                  )}
                </div>
              </div>
              <div className="qty">
                {produto.itemCount > 0 ? (
                  <span title={`Em ${produto.itemCount} lugar(es)`}>
                    {produto.totalQuantity} {produto.unit}
                  </span>
                ) : (
                  <span className="small muted">fora de estoque</span>
                )}
              </div>
              <div className="actions">
                <button type="button" className="btn btn-sm" onClick={() => setEditando(produto)}>
                  Editar
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => void apagar(produto)}
                >
                  Apagar
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {criando ? (
        <ProductModal original={null} onClose={() => setCriando(false)} onSaved={aoSalvar} />
      ) : null}
      {editando ? (
        <ProductModal
          original={editando}
          onClose={() => setEditando(null)}
          onSaved={aoSalvar}
        />
      ) : null}
    </main>
  );
}

/** Cadastro/edicao de um produto, com leitura da embalagem por foto. */
function ProductModal({
  original,
  onClose,
  onSaved,
}: {
  original: Product | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<ProductDraft>(
    original ? productToDraft(original) : { ...EMPTY_PRODUCT_DRAFT },
  );
  const [salvando, setSalvando] = useState(false);

  function aplicarFoto(result: PhotoIdentification) {
    if (result.product && result.product.id !== original?.id) {
      // O codigo lido ja pertence a outro produto: abrir dois cadastros para o
      // mesmo codigo so criaria confusao na proxima leitura.
      toast(`Este codigo ja e do produto "${result.product.name}".`, 'error');
      return;
    }
    if (result.suggestion) {
      setDraft((atual) => applyVisionSuggestion(atual, result.suggestion!, result.barcode));
      toast('Embalagem lida — confira os campos.', 'ok');
    }
  }

  async function salvar() {
    if (needsBarcodeLink(draft)) {
      toast('Informe o codigo de barras ou marque que o produto nao tem um.', 'error');
      return;
    }
    const input = draftToProductInput(draft);
    if (!input.name) {
      toast('Informe ao menos o tipo ou o nome do produto.', 'error');
      return;
    }

    setSalvando(true);
    try {
      if (original) {
        const patch = changedProductFields(draft, original);
        if (!patch) {
          toast('Nada mudou.', 'info');
          onClose();
          return;
        }
        await api.updateProduct(original.id, patch);
        toast('Produto atualizado em todas as dispensas.', 'ok');
      } else {
        const criado = await api.createProduct(input);
        // Codigo ja cadastrado: a API devolve o dono em vez de duplicar.
        toast(
          criado.name === input.name
            ? 'Produto cadastrado.'
            : `Este codigo ja era do produto "${criado.name}" — nada foi duplicado.`,
          criado.name === input.name ? 'ok' : 'info',
        );
      }
      await onSaved();
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal title={original ? `Editar ${original.name}` : 'Novo produto'} onClose={onClose}>
      <ProductFields
        value={draft}
        onChange={setDraft}
        idPrefix="produto"
        autoFocus={!original}
        onPhotoResult={aplicarFoto}
      />
      <p className="small muted">
        A edicao vale para o produto no catalogo — todas as dispensas onde ele estiver.
      </p>
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose}>
          Cancelar
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void salvar()} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </Modal>
  );
}
